import type {
  SeasonalityKeyword,
  TrendKeyword,
  TrendPeriodRow,
  TrendSeriesField,
} from '../models/trendSeasonality.types'

import type { ExportColumn, ExportRow, SheetInput } from '@/shared/lib/export'

type ExportKeyword = TrendKeyword | SeasonalityKeyword

export interface TrendExportLabels {
  filename: string
  sheet: string
  query: string
  month: string
  value: string
  forecast: string
}

export interface ExportTrendDataFlowOptions {
  toXlsx: (sheets: readonly SheetInput[], filename: string) => Promise<void>
}

export interface TrendPeriodExportLabels {
  filename: string
  sheet: string
  query: string
  volume: string
  change: string
}

/** Serializes the active decomposition through module 16's shared XLSX writer contract. */
export class ExportTrendDataFlow {
  constructor(private readonly options: ExportTrendDataFlowOptions) {}

  execute(
    keywords: readonly ExportKeyword[],
    field: TrendSeriesField,
    labels: TrendExportLabels,
  ): Promise<void> {
    const columns: ExportColumn[] = [
      { field: 'query', header: labels.query },
      { field: 'month', header: labels.month },
      { field: 'value', header: labels.value, numeric: true },
      { field: 'forecast', header: labels.forecast },
    ]
    const data: ExportRow[] = keywords.flatMap((keyword) =>
      keyword.data.map((point) => ({
        query: keyword.query,
        month: point.month,
        value: exportValue(point as unknown as Record<string, unknown>, field),
        forecast: 'is_forecasted' in point && point.is_forecasted,
      })),
    )
    return this.options.toXlsx([{ name: labels.sheet, columns, data }], labels.filename)
  }

  executePeriod(rows: readonly TrendPeriodRow[], labels: TrendPeriodExportLabels): Promise<void> {
    const columns: ExportColumn[] = [
      { field: 'query', header: labels.query },
      { field: 'volume', header: labels.volume, numeric: true },
      { field: 'change', header: labels.change, numeric: true },
    ]
    return this.options.toXlsx(
      [{ name: labels.sheet, columns, data: rows.map((row) => ({ ...row })) }],
      labels.filename,
    )
  }
}

function exportValue(point: Record<string, unknown>, field: TrendSeriesField): number | null {
  const value = point[field]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}
